import GridStack from 'gridstack/dist/gridstack-all.js';

const WIDE_ROW_CARDS = 3;
const NARROW_ROW_CARDS = 2;
const WIDE_ROW_CARD_WIDTH = 450;
const SUMMARY_CARD_WIDTH = 350;
export const maxCardsPerRow = containerWidth => (
  containerWidth >= WIDE_ROW_CARDS * WIDE_ROW_CARD_WIDTH ? WIDE_ROW_CARDS : NARROW_ROW_CARDS
);

// left to gridstack, which card moves out of an overlap depends on the grid's previous layout, so a tall card swapped
// places with the card under it on every width change
export function clearOverlaps(layout) {
  const placed = [];
  [...layout].sort((a, b) => a.y - b.y || a.x - b.x).forEach(item => {
    const overlapping = () => placed.filter(p => p.x < item.x + item.w && item.x < p.x + p.w && p.y < item.y + item.h && item.y < p.y + p.h);
    for (let hit = overlapping(); hit.length; hit = overlapping()) {
      item.y = Math.max(...hit.map(p => p.y + p.h));
    }
    placed.push(item);
  });
  return layout;
}

function _groupByY(layout) {
  const byY = {};
  layout.forEach(item => {
    (byY[item.y] = byY[item.y] || []).push(item);
  });
  return Object.keys(byY).sort((a, b) => Number(a) - Number(b)).map(y => byY[y]);
}

export class GridStackManager {
  constructor(gridRef, options = {}) {
    this.gridRef = gridRef;
    this.grid = null;
    this.resizeObserver = null;
    this.lastWidgetsPerRow = null;
    this.widgetConfig = options.widgetConfig || null;
    this.defaultWidgetConfig = options.widgetConfig || null; // Store default config as fallback
    this.widgetIdPrefix = options.widgetIdPrefix || 'card-';
    this.resizeTimer = null;
    this.isCustomizeMode = false; // Track customize mode ourselves
    this.isUpdatingHeight = false; // Flag to prevent layout recalculation during height updates
    this.options = {
      cellHeight: options.cellHeight || 540,
      autoCellHeight: false,
      column: 12,
      draggable: { handle: '.card-drag' },
      ...options
    };
    this.defaultWidgetOrder = [];
    this.laidOutOrder = [];
  }

  // Trigger chart resize with debounce to avoid multiple rapid calls
  triggerChartResize() {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      this.resizeTimer = null;
    }, 150);
  }

  setDefaultWidgetOrder(widgets) {
    this.defaultWidgetOrder = [...widgets];
  }

  setWidgetConfig(widgetConfig) {
    this.widgetConfig = widgetConfig;
  }

  // Restore widget configuration from saved layout data
  restoreFromSavedOrder(savedData, defaultConfig) {
    // Always store the default config as fallback for widgets not in saved layout
    this.defaultWidgetConfig = defaultConfig;

    if (!savedData) {
      this.setWidgetConfig(defaultConfig);
      return;
    }

    // Handle both old format (array of widget IDs) and new format (layout data)
    if (Array.isArray(savedData) && typeof savedData[0] === 'string') {
      // Old format - just widget order
      // Start with all default widgets, then override with saved positions
      const restoredConfig = { ...defaultConfig };
      let currentRow = 1;
      let widgetsInCurrentRow = 0;
      const maxWidgetsPerRow = 3;

      savedData.forEach((widgetId) => {
        const originalWidget = defaultConfig[widgetId];
        if (originalWidget) {
          if (widgetsInCurrentRow >= maxWidgetsPerRow) {
            currentRow++;
            widgetsInCurrentRow = 0;
          }

          restoredConfig[widgetId] = {
            ...originalWidget,
            row: currentRow
          };

          widgetsInCurrentRow++;
        }
      });

      this.setWidgetConfig(restoredConfig);
    } else if (savedData.layoutData && savedData.layoutData.length > 0) {
      // New format - complete layout data
      // Start with all default widgets, then override with saved positions
      const restoredConfig = { ...defaultConfig };

      savedData.layoutData.forEach(item => {
        const originalWidget = defaultConfig[item.id];
        if (originalWidget) {
          restoredConfig[item.id] = {
            title: originalWidget.title,
            w: item.w,
            h: item.h,
            isMetric: originalWidget.isMetric || false,
            linkTo: originalWidget.linkTo,
            row: item.row,
            x: item.x,
            y: item.y
          };
        }
      });

      this.setWidgetConfig(restoredConfig);
    } else {
      // Fallback to default
      this.setWidgetConfig(defaultConfig);
    }
  }

  cardsPerRow(containerWidth) {
    return this.options.fixedRows ? Infinity : maxCardsPerRow(containerWidth);
  }

  summaryRow(ordered, containerWidth) {
    if (!this.options.isSummaryCard || containerWidth < WIDE_ROW_CARDS * SUMMARY_CARD_WIDTH) return [];
    const firstOther = ordered.findIndex(widgetId => !this.options.isSummaryCard(widgetId.replace(this.widgetIdPrefix, '')));
    return ordered.slice(0, Math.min(firstOther === -1 ? ordered.length : firstOther, WIDE_ROW_CARDS));
  }

  widgetRow(widgetId, config) {
    const cleanWidgetId = widgetId.replace(this.widgetIdPrefix, '');
    const widget = config[cleanWidgetId] || (this.defaultWidgetConfig && this.defaultWidgetConfig[cleanWidgetId]);
    return widget ? widget.row : 1;
  }

  calculateResponsiveLayout(selectedWidgets, widgetConfig = null) {
    const containerWidth = this.gridRef.current ? this.gridRef.current.offsetWidth : 1200;
    
    const layout = [];
    const config = widgetConfig || this.widgetConfig;
    
    if (containerWidth < (this.options.minWidgetWidth ? 2 * this.options.minWidgetWidth : 840)) {
      const stacked = config ? [...selectedWidgets].sort((a, b) => this.widgetRow(a, config) - this.widgetRow(b, config)) : selectedWidgets;
      stacked.forEach((widgetId, index) => {
        layout.push({
          id: widgetId,
          x: 0,
          y: index,
          w: 12,
          h: 1
        });
      });
      return layout;
    }
    
    // For larger screens, use the widget configuration
    if (!config) {
      // Fallback dynamic calculation for larger screens
      const widgetsPerRow = this.cardsPerRow(containerWidth);
      const numRows = Math.ceil(selectedWidgets.length / widgetsPerRow);
      let currentIndex = 0;
      
      for (let row = 0; row < numRows; row++) {
        const widgetsInRow = Math.min(widgetsPerRow, selectedWidgets.length - currentIndex);
        const adjustedColumnWidth = 12 / widgetsInRow;
        
        for (let col = 0; col < widgetsInRow; col++) {
          if (currentIndex < selectedWidgets.length) {
            layout.push({
              id: selectedWidgets[currentIndex],
              x: col * adjustedColumnWidth,
              y: row,
              w: adjustedColumnWidth,
              h: 1
            });
            currentIndex++;
          }
        }
      }
      return layout;
    }
    
    // Group widgets by their fixed row positions
    const widgetsByRow = {};

    selectedWidgets.forEach(widgetId => {
      const row = this.widgetRow(widgetId, config);

      if (!widgetsByRow[row]) {
        widgetsByRow[row] = [];
      }
      widgetsByRow[row].push(widgetId);
    });
    
    // Sort rows and create layout
    const sortedRows = Object.keys(widgetsByRow).sort((a, b) => parseInt(a) - parseInt(b));

    const perRow = this.cardsPerRow(containerWidth);
    const ordered = sortedRows.flatMap(row => widgetsByRow[row]);
    const summary = this.summaryRow(ordered, containerWidth);
    if (summary.length || sortedRows.some(row => widgetsByRow[row].length > perRow)) {
      summary.forEach((widgetId, index) => {
        const columnWidth = 12 / summary.length;
        layout.push({ id: widgetId, x: index * columnWidth, y: 0, w: columnWidth, h: 1 });
      });
      const rest = ordered.slice(summary.length);
      const firstRow = summary.length ? 1 : 0;
      rest.forEach((widgetId, index) => {
        const rowIndex = Math.floor(index / perRow);
        const widgetsInRow = Math.min(perRow, rest.length - rowIndex * perRow);
        const columnWidth = 12 / widgetsInRow;
        layout.push({ id: widgetId, x: (index % perRow) * columnWidth, y: firstRow + rowIndex, w: columnWidth, h: 1 });
      });
      return layout;
    }
    
    sortedRows.forEach((row, rowIndex) => {
      const rowWidgets = widgetsByRow[row];
      const adjustedColumnWidth = 12 / rowWidgets.length;
      
      rowWidgets.forEach((widgetId, indexInRow) => {
        layout.push({
          id: widgetId,
          x: indexInRow * adjustedColumnWidth,
          y: rowIndex,
          w: adjustedColumnWidth,
          h: 1
        });
      });
    });
    
    return layout;
  }

  applyRowHeights(layout) {
    if (!this.options.widgetHeight) return layout;
    const rows = _groupByY(layout);
    let offset = 0;
    rows.forEach(row => {
      const height = Math.max(...row.map(item => this.options.widgetHeight(String(item.id).replace(this.widgetIdPrefix, ''))));
      row.forEach(item => {
        item.y = offset;
        item.h = height;
      });
      offset += height;
    });
    return layout;
  }

  async loadGridStack() {
    if (this.grid || !this.gridRef.current) return;

    this.grid = GridStack.init({
      ...this.options,
      staticGrid: true,
    }, this.gridRef.current);

    this.setupDragAndDrop();
    this.setupResizeObserver();

    return this.grid;
  }

  setupResizeObserver() {
    if (!this.grid || !this.gridRef.current) return;

    this.updateColumnCount();

    this.resizeObserver = new ResizeObserver(() => {
      this.updateColumnCount();
    });

    this.resizeObserver.observe(this.gridRef.current);
  }

  withoutAnimation(update) {
    if (!this.grid || !this.gridRef.current) {
      update();
      return;
    }
    const { animate } = this.grid.opts;
    this.grid.setAnimation(false);
    try {
      update();
    } finally {
      // lays the new heights out before the animation comes back, so they don't animate after it
      this.gridRef.current.getBoundingClientRect();
      this.grid.setAnimation(animate);
    }
  }

  updateColumnCount() {
    if (!this.grid || !this.gridRef.current || this.isDragging || this.isUpdatingHeight) return;

    const inGrid = this.grid.engine.nodes.map(node => node.el.id.replace(this.widgetIdPrefix, ''));
    this.recalculateLayout(this.isCustomizeMode ? inGrid : this.inLaidOutOrder(inGrid));
  }

  // gridstack reorders its own nodes when a card taller than its row collides with the cards under it, and a layout made
  // from that order moved cards between columns on every resize; while customizing, the dragged order is the grid's
  inLaidOutOrder(ids) {
    const rendered = Array.from(this.gridRef.current.querySelectorAll('.grid-stack-item'))
      .map(item => String(item.id).replace(this.widgetIdPrefix, ''));
    return [this.laidOutOrder, rendered]
      .map(order => order.filter(id => ids.includes(id)))
      .find(order => order.length === ids.length) || ids;
  }

  recalculateLayout(selectedWidgets) {
    if (!this.grid) return;

    this.laidOutOrder = selectedWidgets;
    if (selectedWidgets.length > 0) {
      const heights = this.grid.engine.nodes.reduce((map, node) => {
        map[node.el.id] = node.h;
        return map;
      }, {});
      this.grid.removeAll(false);
      const layout = this.options.widgetHeight
        ? this.applyRowHeights(this.calculateResponsiveLayout(selectedWidgets))
        : clearOverlaps(this.calculateResponsiveLayout(selectedWidgets).map(item => ({
          ...item,
          h: heights[`${this.widgetIdPrefix}${item.id}`] || heights[item.id] || item.h
        })));

      const allWidgets = Array.from(this.gridRef.current.querySelectorAll('.grid-stack-item'));
      const widgetMap = allWidgets.reduce((map, widget) => {
        map[widget.id] = widget;
        return map;
      }, {});

      layout.forEach(item => {
        const widgetId = `${this.widgetIdPrefix}${item.id}`;
        const widget = widgetMap[widgetId] || widgetMap[item.id];
        if (widget) {
          // Remove any existing grid attributes
          widget.removeAttribute('gs-x');
          widget.removeAttribute('gs-y');
          widget.removeAttribute('gs-w');
          widget.removeAttribute('gs-h');

          this.grid.makeWidget(widget, {
            x: item.x,
            y: item.y,
            w: item.w,
            h: item.h,
            draggable: true
          });
        }
      });

      // Force a layout update
      this.grid.batchUpdate(false);
      if (this.options.onLayout) this.options.onLayout();
    }
  }

  updateWidgets(selectedWidgets) {
    this.recalculateLayout(selectedWidgets);
  }

  // Update widget height without triggering full layout recalculation
  updateWidgetHeight(element, newHeight) {
    if (!this.grid || !element) return;

    this.isUpdatingHeight = true;
    try {
      this.grid.update(element, { h: newHeight });
    } finally {
      // Use setTimeout to ensure the flag is reset after any async operations
      setTimeout(() => {
        this.isUpdatingHeight = false;
      }, 100);
    }
  }

  handleCustomizeViewClick = (selectedWidgets) => {
    if (!this.grid) return;
    this.isCustomizeMode = true;
    this.grid.setStatic(false);
    this.recalculateLayout(selectedWidgets);
    // Re-setup drag and drop handlers after layout recalculation
    this.setupDragAndDrop();
  };

  handleGoBackFromCustomize = (selectedWidgets, callback) => {
    if (!this.grid) {
      if (callback) callback();
      return;
    }
    this.isCustomizeMode = false;
    this.grid.setStatic(true);
    this.recalculateLayout(selectedWidgets);
    //this.triggerChartResize();
    if (callback) {
      callback();
    }
  };

  handleApplyCustomization = () => {
    if (!this.grid) return { widgets: [], layoutData: [] };

    // Get current widget positions and create complete layout data
    const currentNodes = this.grid.engine.nodes
      .sort((a, b) => {
        if (a.y === b.y) {
          return a.x - b.x;
        }
        return a.y - b.y;
      });

    // If no nodes, return empty data
    if (!currentNodes || currentNodes.length === 0) {
      return { widgets: [], layoutData: [] };
    }

    // Create layout data with position and size information
    const layoutData = currentNodes.map((node, index) => {
      const widgetId = node.el.id.replace(this.widgetIdPrefix, '');
      return {
        id: widgetId,
        order: index,
        x: node.x,
        y: node.y,
        w: node.w,
        h: node.h,
        row: node.y + 1 // Convert 0-based y to 1-based row
      };
    });

    // Update the widget configuration with new layout
    const newConfig = {};
    layoutData.forEach(item => {
      // Try current config first, then fall back to default config
      const originalWidget = (this.widgetConfig && this.widgetConfig[item.id]) || (this.defaultWidgetConfig && this.defaultWidgetConfig[item.id]);
      newConfig[item.id] = {
        title: originalWidget?.title || item.id,
        w: item.w,
        h: item.h,
        isMetric: originalWidget?.isMetric || false,
        linkTo: originalWidget?.linkTo,
        row: item.row,
        x: item.x,
        y: item.y
      };
    });

    // Update the widget configuration
    this.setWidgetConfig(newConfig);
    
    // Return complete layout data for saving
    const orderedWidgets = layoutData.map(item => item.id);
    
    this.handleGoBackFromCustomize(orderedWidgets);
    return { widgets: orderedWidgets, layoutData };
  };

  handleTempWidgetSelectionChange = (selectedWidgets) => {
    if (!this.grid) return;

    // Must recalculate layout so remaining widgets fill the row
    // Set non-static BEFORE recalculating so widgets are created as draggable
    if (this.isCustomizeMode) {
      this.grid.setStatic(false);
    }

    this.recalculateLayout(selectedWidgets);

    // Re-setup drag and drop handlers after layout recalculation
    if (this.isCustomizeMode) {
      this.grid.setStatic(false);
      this.setupDragAndDrop();
    } else {
      // Only trigger chart resize when NOT in customize mode
      // In customize mode, this causes a loop that recalculates layout and undoes swaps
      this.triggerChartResize();
    }
  };

  setupDragAndDrop() {
    if (!this.grid) return;

    // Clear all existing drag events
    this.grid.off('dragstop');
    this.grid.off('dragstart');
    this.grid.off('dropped');

    // Track drag state
    this.isDragging = false;
    let draggedElement = null;
    let originalPositions = {};

    this.grid.on('dragstart', (event, el) => {
      this.isDragging = true;
      draggedElement = el;

      // Store original positions of all widgets before drag
      originalPositions = {};
      this.grid.engine.nodes.forEach(node => {
        originalPositions[node.el.id] = {
          x: node.x,
          y: node.y,
          w: node.w,
          h: node.h
        };
      });
    });

    this.grid.on('dragstop', (event, el) => {
      if (!draggedElement || !originalPositions[draggedElement.id]) {
        this.isDragging = false;
        draggedElement = null;
        originalPositions = {};
        return;
      }

      const draggedOriginal = originalPositions[draggedElement.id];
      const draggedNode = this.grid.engine.nodes.find(node => node.el === draggedElement);

      if (!draggedNode) {
        this.isDragging = false;
        draggedElement = null;
        originalPositions = {};
        return;
      }

      // Find the widget we're swapping with based on current position
      let targetNode = null;
      let targetOriginal = null;
      let bestScore = -1;

      // Calculate center of dragged widget's new position
      const draggedCenterX = draggedNode.x + draggedNode.w / 2;
      const draggedCenterY = draggedNode.y + draggedNode.h / 2;

      for (const node of this.grid.engine.nodes) {
        if (node.el === draggedElement) continue;

        const original = originalPositions[node.el.id];
        if (!original) continue;

        // Calculate center of this widget's original position
        const targetCenterX = original.x + original.w / 2;
        const targetCenterY = original.y + original.h / 2;

        // Check if dragged widget overlaps with this widget's original position
        const overlapX = Math.max(0,
          Math.min(draggedNode.x + draggedNode.w, original.x + original.w) -
          Math.max(draggedNode.x, original.x)
        );
        const overlapY = Math.max(0,
          Math.min(draggedNode.y + draggedNode.h, original.y + original.h) -
          Math.max(draggedNode.y, original.y)
        );

        // Calculate distance between centers
        const distance = Math.sqrt(
          Math.pow(draggedCenterX - targetCenterX, 2) +
          Math.pow(draggedCenterY - targetCenterY, 2)
        );

        // Score based on overlap (primary) and proximity (secondary)
        // Higher overlap = better, closer distance = better
        const overlapArea = overlapX * overlapY;
        const proximityScore = overlapArea > 0 ? 1000 + overlapArea : (distance < 2 ? 100 - distance * 10 : 0);

        if (proximityScore > bestScore) {
          bestScore = proximityScore;
          targetNode = node;
          targetOriginal = original;
        }
      }

      // Check if widget actually moved
      const hasMoved = draggedNode.x !== draggedOriginal.x || draggedNode.y !== draggedOriginal.y;

      if (targetNode && targetOriginal && hasMoved && bestScore > 0) {
        // Swap: dragged widget takes target's original position and width
        // Target widget takes dragged widget's original position and width
        this.grid.batchUpdate(true);

        this.grid.update(draggedElement, {
          x: targetOriginal.x,
          y: targetOriginal.y,
          w: targetOriginal.w,
          h: targetOriginal.h
        });

        this.grid.update(targetNode.el, {
          x: draggedOriginal.x,
          y: draggedOriginal.y,
          w: draggedOriginal.w,
          h: draggedOriginal.h
        });

        this.grid.batchUpdate(false);

        // Update widget config with new row assignments
        const draggedId = draggedElement.id.replace(this.widgetIdPrefix, '');
        const targetId = targetNode.el.id.replace(this.widgetIdPrefix, '');

        if (this.widgetConfig) {
          // Swap row assignments in config
          const draggedRow = this.widgetConfig[draggedId]?.row;
          const targetRow = this.widgetConfig[targetId]?.row;

          if (this.widgetConfig[draggedId]) {
            this.widgetConfig[draggedId].row = targetRow || (targetOriginal.y + 1);
            this.widgetConfig[draggedId].x = targetOriginal.x;
          }
          if (this.widgetConfig[targetId]) {
            this.widgetConfig[targetId].row = draggedRow || (draggedOriginal.y + 1);
            this.widgetConfig[targetId].x = draggedOriginal.x;
          }
        }
      } else {
        // No valid swap target or didn't move - restore original position
        this.grid.update(draggedElement, {
          x: draggedOriginal.x,
          y: draggedOriginal.y,
          w: draggedOriginal.w,
          h: draggedOriginal.h
        });
      }

      this.isDragging = false;
      draggedElement = null;
      originalPositions = {};
    });
  }

  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.grid) {
      this.grid.destroy(true); // true to remove DOM elements and widgets
      this.grid = null;
    }
  }

  // Clean up JS resources without manipulating DOM (let React handle DOM cleanup)
  cleanupWithoutDom() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.grid) {
      this.grid.destroy(false); // false = don't remove DOM elements
      this.grid = null;
    }
  }
}
